import { useCallback, useSyncExternalStore } from 'react'

/**
 * Subscribe to a CSS media query and return whether it matches.
 *
 * Uses `useSyncExternalStore` — the canonical React pattern for browser-native
 * subscription APIs. Re-subscribes when `query` changes and returns `false`
 * during SSR (when `window` is undefined).
 *
 * @param query - A valid CSS media query string, e.g. `'(min-width: 1024px)'`
 * @returns `true` when the viewport matches the query, `false` otherwise.
 */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback((onChange: () => void) => {
    const mql = window.matchMedia(query)
    mql.addEventListener('change', onChange)
    return () => { mql.removeEventListener('change', onChange); }
  }, [query])

  const getSnapshot = useCallback(() => window.matchMedia(query).matches, [query])
  const getServerSnapshot = useCallback(() => false, [])

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
}
